import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatNumber } from '../../utils/formatters.js';

export function FulfillOrderModal({ order, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [fulfillments, setFulfillments] = useState({});
  const [error, setError] = useState(null);
  const [ship, setShip] = useState({
    carrier: '', trackingNumber: '', packageCount: '', totalWeight: '',
    deliveryDate: new Date().toISOString().slice(0, 10), shippingDate: '',
  });
  const setShipField = (k) => (e) => setShip((p) => ({ ...p, [k]: e.target.value }));

  const detail = useQuery({
    queryKey: ['sales', 'orders', order?.id],
    queryFn: () => salesApi.getOrder(order.id).then((r) => r.items),
    enabled: Boolean(order),
  });

  useEffect(() => {
    if (detail.data?.lines) {
      const initial = {};
      for (const line of detail.data.lines) {
        const outstanding = Number(line.quantity) - Number(line.fulfilledQuantity);
        initial[line.id] = outstanding > 0 ? outstanding.toString() : '0';
      }
      setFulfillments(initial);
      setError(null);
    }
  }, [detail.data]);

  const fulfill = useMutation({
    mutationFn: ({ id, body }) => salesApi.fulfillOrder(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sales', 'deliveries'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    const lines = (detail.data?.lines || [])
      .map((line) => {
        const qty = Number(fulfillments[line.id] || 0);
        return qty > 0 ? { lineId: line.id, quantity: qty } : null;
      })
      .filter(Boolean);
    if (lines.length === 0) {
      setError(t('sales.orders.fulfill.intro'));
      return;
    }
    const body = { lines };
    if (ship.carrier) body.carrier = ship.carrier;
    if (ship.trackingNumber) body.trackingNumber = ship.trackingNumber;
    if (ship.packageCount !== '') body.packageCount = Number(ship.packageCount);
    if (ship.totalWeight !== '') body.totalWeight = Number(ship.totalWeight);
    if (ship.deliveryDate) body.deliveryDate = ship.deliveryDate;
    if (ship.shippingDate) body.shippingDate = ship.shippingDate;
    fulfill.mutate({ id: order.id, body });
  }

  return (
    <Modal
      open={Boolean(order)}
      onClose={onClose}
      title={t('sales.orders.fulfill.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={fulfill.isPending}>
            {fulfill.isPending ? `${t('common.save')}…` : t('sales.orders.action.fulfill')}
          </Button>
        </>
      }
    >
      {order && (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <p className="muted text-sm" style={{ margin: 0 }}>
            <strong>{order.orderNumber}</strong> - {t('sales.orders.fulfill.intro')}
          </p>
          {(detail.data?.lines || []).map((line) => {
            const outstanding = Number(line.quantity) - Number(line.fulfilledQuantity);
            return (
              <div
                key={line.id}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
                  padding: 'var(--space-3)', border: '1px solid var(--color-hairline)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <strong>{line.description}</strong>
                <div className="muted text-xs" style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  <span>{t('sales.orders.fulfill.ordered')}: <span className="mono">{formatNumber(line.quantity)}</span></span>
                  <span>{t('sales.orders.fulfill.already')}: <span className="mono">{formatNumber(line.fulfilledQuantity)}</span></span>
                  <span>{t('sales.orders.fulfill.outstanding')}: <span className="mono">{formatNumber(outstanding)}</span></span>
                </div>
                <label className="field">
                  <span className="field-label">{t('sales.orders.fulfill.now')}</span>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    max={outstanding}
                    step="any"
                    value={fulfillments[line.id] ?? '0'}
                    disabled={outstanding <= 0}
                    onChange={(e) => setFulfillments((p) => ({ ...p, [line.id]: e.target.value }))}
                  />
                </label>
              </div>
            );
          })}
          <fieldset style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)', border: '1px solid var(--color-hairline)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)' }}>
            <legend className="field-label">{t('delivery.shipping')}</legend>
            <label className="field">
              <span className="field-label">{t('delivery.carrier')}</span>
              <input className="input" value={ship.carrier} onChange={setShipField('carrier')} />
            </label>
            <label className="field">
              <span className="field-label">{t('delivery.tracking')}</span>
              <input className="input" value={ship.trackingNumber} onChange={setShipField('trackingNumber')} />
            </label>
            <label className="field">
              <span className="field-label">{t('delivery.packages')}</span>
              <input className="input" type="number" min="0" value={ship.packageCount} onChange={setShipField('packageCount')} />
            </label>
            <label className="field">
              <span className="field-label">{t('delivery.weight')}</span>
              <input className="input" type="number" min="0" step="0.001" value={ship.totalWeight} onChange={setShipField('totalWeight')} />
            </label>
            <label className="field">
              <span className="field-label">{t('delivery.deliveryDate')}</span>
              <input className="input" type="date" value={ship.deliveryDate} onChange={setShipField('deliveryDate')} />
            </label>
            <label className="field">
              <span className="field-label">{t('delivery.shippingDate')}</span>
              <input className="input" type="date" value={ship.shippingDate} onChange={setShipField('shippingDate')} />
            </label>
          </fieldset>
          {error && <div className="alert danger">{error}</div>}
        </form>
      )}
    </Modal>
  );
}
